import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { SupportChatHandle, SupportTicketPrefill } from '../../../shared/support-chat';
import { track } from '@/lib/analytics';
import { config } from '@/lib/config';
import { dashboardWelcome, launcherBottom, launcherShown, PHONE_LAYOUT, supportChatOptions } from '@/lib/support-chat';
import { useWorkspace } from '@/app/workspace';

function subscribePhoneLayout(onChange: () => void) {
  const media = window.matchMedia(PHONE_LAYOUT);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

interface SupportChat {
  /** Opens the chat. `prefill` fills the message box; nothing is sent until the person sends it. */
  open: (prefill?: string) => void;
  /** Opens the chat's ticket form, with a topic and a description the person can edit before raising it. */
  openTicket: (prefill?: SupportTicketPrefill) => void;
  /** Keeps the launcher clear of `element` (a bar fixed to the bottom of the screen) until the returned cleanup runs. */
  avoid: (element: HTMLElement) => () => void;
  /** Re-checks the launcher after an avoided element moved without resizing. */
  moved: () => void;
}

const SupportChatContext = createContext<SupportChat | null>(null);

/**
 * The support chat for every signed-in page, /welcome included. Unmounting it (sign-out) removes the widget. On /chat
 * (the widget's "Open in a new tab") it fills the window instead of floating.
 */
export function SupportChatProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const { pathname } = useLocation();
  const standalone = pathname.replace(/\/+$/, '') === '/chat';
  const navigate = useNavigate();
  const path = useRef(pathname);
  const go = useRef(navigate);
  useEffect(() => {
    path.current = pathname;
    go.current = navigate;
  }, [pathname, navigate]);
  const handle = useRef<SupportChatHandle | null>(null);
  const avoided = useRef(new Set<HTMLElement>());
  const observer = useRef<ResizeObserver | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pending = useRef<((chat: SupportChatHandle) => void) | null>(null);
  const [broken, setBroken] = useState(false);
  // Read when the widget mounts; a refreshed workspace never remounts the chat.
  const { user, org } = useWorkspace();
  const welcome = useRef(dashboardWelcome(user, org.creatorId));
  welcome.current = dashboardWelcome(user, org.creatorId);
  const phoneLayout = useSyncExternalStore(
    subscribePhoneLayout,
    () => window.matchMedia(PHONE_LAYOUT).matches,
    () => false,
  );
  const launcher = launcherShown(pathname, phoneLayout);
  const showLauncher = useRef(launcher);
  useEffect(() => {
    showLauncher.current = launcher;
    handle.current?.setLauncher(launcher);
  }, [launcher]);

  const clearance = useCallback(
    () =>
      launcherBottom(
        [...avoided.current].map((element) => element.getBoundingClientRect()),
        { width: window.innerWidth, height: window.innerHeight },
      ),
    [],
  );
  // The widget moves its launcher (and the floating panel above it) in place, open or closed, so a conversation
  // someone is reading or typing in is never reloaded when a bar appears under it.
  const check = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => handle.current?.setOffset({ bottom: clearance() }), 150);
  }, [clearance]);

  useEffect(() => {
    let live = true;
    let chat: SupportChatHandle | null = null;
    // Loaded on demand: the entry chunk also serves /signup, which ad visitors on slow phones wait for.
    import('../../../shared/support-chat')
      .then(({ mountSupportChat }) => {
        if (!live) return;
        // Bars to avoid register through callback refs, which are attached by now, so the launcher starts clear of them.
        chat = mountSupportChat(
          supportChatOptions({
            demo: config.isDemo,
            userId,
            endpoint: config.communicationApi,
            bottom: clearance(),
            // Only the path: queries here can hold searched phone numbers and names.
            page: () => path.current,
            onEvent: (name, detail) => track(name, detail),
            // An assistant link to a dashboard page routes in this tab instead of loading the app again in another.
            onNavigate: (to) => go.current(to),
            standalone,
            launcher: showLauncher.current,
            welcome: welcome.current,
          }),
        );
        handle.current = chat;
        const queued = pending.current;
        pending.current = null;
        queued?.(chat);
      })
      // Without the widget, the entry points fall back to Help's prepared WhatsApp and email message.
      .catch(() => live && setBroken(true));
    return () => {
      live = false;
      chat?.destroy();
      handle.current = null;
    };
  }, [userId, clearance, standalone]);

  useEffect(() => {
    const resized = () => check();
    observer.current = typeof ResizeObserver === 'function' ? new ResizeObserver(resized) : null;
    avoided.current.forEach((element) => observer.current?.observe(element));
    window.addEventListener('resize', resized);
    return () => {
      window.removeEventListener('resize', resized);
      observer.current?.disconnect();
      observer.current = null;
      clearTimeout(timer.current);
    };
  }, [check]);

  const value = useMemo<SupportChat>(() => {
    // Radix menus and dialogs hand focus back to their trigger in a timeout after closing. Opening after that
    // timeout keeps focus in the chat when it is opened from a menu item or the command menu.
    const later = (run: (chat: SupportChatHandle) => void) =>
      setTimeout(() =>
        setTimeout(() => {
          if (handle.current) run(handle.current);
          // Still loading: it opens as soon as it mounts.
          else pending.current = run;
        }),
      );
    return {
      open: (prefill) => later((chat) => chat.open(prefill)),
      openTicket: (prefill) => later((chat) => chat.openTicket(prefill)),
      avoid: (element) => {
        avoided.current.add(element);
        observer.current?.observe(element);
        check();
        return () => {
          avoided.current.delete(element);
          observer.current?.unobserve(element);
          check();
        };
      },
      moved: () => check(),
    };
  }, [check]);
  return <SupportChatContext.Provider value={broken ? null : value}>{children}</SupportChatContext.Provider>;
}

/** Null outside the signed-in app or when the widget failed to load, so callers can fall back to another way of reaching support. */
export function useSupportChat() {
  return useContext(SupportChatContext);
}

/**
 * A callback ref for a bar fixed to the bottom of the screen: the chat launcher moves up while it is mounted.
 * Pass the bar's position when it can be dragged, since moving it does not resize it.
 */
export function useChatClearance(position?: unknown) {
  const chat = useContext(SupportChatContext);
  useEffect(() => {
    chat?.moved();
  }, [chat, position]);
  return useCallback(
    (element: HTMLElement | null) => (chat && element ? chat.avoid(element) : undefined),
    [chat],
  );
}
